// pages/ProductsPage/ProductsPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  FaDownload,
  FaBell,
  FaFilePdf,
  FaVideo,
  FaLock,
  FaCheck,
  FaClock,
  FaSignal,
} from 'react-icons/fa';
import axios from 'axios';
import { API_BASE } from '../../config/api';
import './css/ProductsPage.css';
import FallbackImg from '../images/landing.webp';

const ProductsPage = () => {
  const [filter, setFilter] = useState('all');

  const [products, setProducts] = useState([]);

  const toAbsoluteImageUrl = (maybeUrl) => {
    if (!maybeUrl) return '';
    const u = String(maybeUrl).trim();
    if (!u) return '';
    if (u.startsWith('http://') || u.startsWith('https://')) return u;
    return `${API_BASE}${u}`;
  };

  const getImageSrc = (product) => {
    const src = toAbsoluteImageUrl(product?.image_url);
    return src || FallbackImg;
  };

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API_BASE}/api/products?limit=200`)
      .then((res) => {
        if (!mounted) return;
        setProducts(Array.isArray(res.data?.products) ? res.data.products : []);
      })
      .catch(() => {
        if (!mounted) return;
        setProducts([]);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const filteredProducts = useMemo(() => {
    if (filter === 'all') return products;
    if (filter === 'free')
      return products.filter((p) => (p.price_text || '').trim() === 'مجاني');
    return products.filter((p) => p.category === filter);
  }, [products, filter]);

  return (
    <div className="products-page" dir="rtl">
      
      {/* 1. Header */}
      <header className="products-header">
        <div className="container text-center relative-z">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            المنتجات الرقمية
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            أدوات، دورات، وملفات مساعدة لرحلتك الأكاديمية (قريباً).
          </motion.p>
        </div>
      </header>

      {/* 2. Filter Bar */}
      <div className="filter-bar">
        <div className="container">
          <div className="filter-buttons">
            <button className={filter === 'all' ? 'active' : ''} onClick={() => setFilter('all')}>الكل</button>
            <button className={filter === 'free' ? 'active' : ''} onClick={() => setFilter('free')}>مجاني</button>
            <button className={filter === 'course' ? 'active' : ''} onClick={() => setFilter('course')}>دورات</button>
            <button className={filter === 'ebook' ? 'active' : ''} onClick={() => setFilter('ebook')}>كتب إلكترونية</button>
          </div>
        </div>
      </div>

      {/* 3. Products Grid */}
      <section className="products-grid">
        <div className="container">
          <motion.div 
            className="row g-4"
            layout
          >
            {filteredProducts.map((product) => (
              <div className="col-lg-3 col-md-6 col-sm-12" key={product.id}>
                <motion.div 
                  className={`product-card ${product.status === 'coming_soon' ? 'coming-soon' : ''}`}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4 }}
                >
                  
                  {/* Image & Badge */}
                  <div className="product-image">
                    <img
                      src={getImageSrc(product)}
                      alt={product.title}
                    />
                    {product.status === 'coming_soon' && (
                      <div className="overlay-lock">
                        <FaLock /> <span>قريباً</span>
                      </div>
                    )}
                    <div className={`status-badge ${product.status}`}>
                      {product.status === 'available' ? 'متاح للتحميل' : 'قريباً'}
                    </div>
                  </div>

                  {/* Body */}
                  <div className="product-body">
                    <div className="product-meta">
                      <span className="type-icon">
                        {product.type === 'pdf' && <FaFilePdf />}
                        {product.type === 'video' && <FaVideo />}
                        {product.type === 'word' && <FaFilePdf />}
                      </span>
                      <span className="product-price">
                        {product.price_text || 'قريباً'}
                      </span>
                    </div>

                    {/* Match ProductsSection meta (duration + level) */}
                    <div className="product-meta" style={{ marginTop: 8 }}>
                      <span className="type-icon" style={{ display: 'inline-flex', gap: 8 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <FaClock /> {product.duration_text || '-'}
                        </span>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <FaSignal /> {product.level_text || '-'}
                        </span>
                      </span>
                      <span className="product-price">{product.category || '-'}</span>
                    </div>
                    
                    <h3 className="product-title">{product.title}</h3>
                    <p className="product-desc">{product.short_desc || ''}</p>
                    
                    {/* Action Button */}
                    <div className="product-action">
                      {product.status === 'available' ? (
                        <button className="btn-product btn-download">
                          <FaDownload /> تحميل الآن
                        </button>
                      ) : (
                        <button className="btn-product btn-notify">
                          <FaBell /> أعلمني عند التوفر
                        </button>
                      )}
                    </div>
                  </div>

                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 4. Newsletter / Waitlist Section */}
      <section className="waitlist-section">
        <div className="container text-center">
          <div className="waitlist-box">
            <FaBell className="bell-icon-large" />
            <h2>لا تفوتك المنتجات الجديدة!</h2>
            <p>
              أقوم حاليًا بإعداد محتوى أكاديمي مكثف سيختصر عليكِ الكثير. 
              سجلي في القائمة البريدية لتكوني أول من يحصل على الخصم عند الإطلاق.
            </p>
            <form className="waitlist-form">
              <input type="email" placeholder="بريدك الإلكتروني الجامعي" />
              <button type="submit">انضمي للقائمة</button>
            </form>
            <p className="spam-note"><FaCheck /> نعدك بعدم إرسال أي بريد مزعج.</p>
          </div>
        </div>
      </section>

    </div>
  );
};

export default ProductsPage;